/**
 * Details (the prototype's `details()`): current folder and who placed it ("Moved by" links to the exact
 * record), an open correction with Resolve / Ignore (web.md §4 additions), topic relevance — with every
 * model's score when more than one scored, "Scored by" naming the shown model, and what a pending score
 * waits for in words (models.md §12) — source
 * history (personal usefulness), the timeline (stored + derived entries, cascades in chain order),
 * filter results (Pending / No match / Matched) and the written assessment.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import type { Correction, MessageDetail, MessageRow } from '@digester/core';
import { reportError } from '../app/errors.js';
import { showToast } from '../app/Toast.js';
import { useEngine } from '../engine-client/EngineProvider.js';
import { Icon } from '../icons/Icon.js';
import { FolderIcon } from '../ui/FolderIcon.js';
import { formatExact } from '../ui/format.js';
import { Tool } from '../ui/Tool.js';
import type { Lookups } from './data.js';
import { eventTime } from './format.js';
import { actorLink, settingsPath, type ActorLink } from './links.js';
import { restoreAndRememberScroll } from './scroll-memory.js';
import { orderTimeline } from './timeline.js';
import { installErrorText, megabytes, waitText } from '../settings/models/catalog.js';
import { isOnDevice, useCatalog, useInstalls } from '../settings/models/data.js';
import { useModelDownload } from '../settings/models/InstallBlock.js';

export interface DetailsPaneProps {
  row: MessageRow | null;
  detail: MessageDetail | undefined;
  lookups: Lookups;
  timeZone: string | undefined;
  scrollKey: string;
  onClose(): void;
  /** Open a Settings record (pushes history; Back returns here). */
  go(to: string): void;
}

const TIMELINE_ICON: Record<string, { cls: string; icon: string }> = {
  source: { cls: 'source', icon: 'rss' },
  filter: { cls: 'filter', icon: 'list-filter' },
  scoring: { cls: 'model', icon: 'sparkles' },
  mcp: { cls: 'model', icon: 'brain' },
  user: { cls: 'user', icon: 'user' },
  system: { cls: 'user', icon: 'settings' },
};

function LinkButton({ link, go, className }: { link: ActorLink; go(to: string): void; className?: string }) {
  if (!link.to) return <strong>{link.label}</strong>;
  const to = link.to;
  return (
    <button type="button" className={className} onClick={() => go(to)}>
      {link.label} <Icon name="external-link" />
    </button>
  );
}

function CorrectionSection({ correction, lookups, go }: { correction: Correction; lookups: Lookups; go(to: string): void }) {
  const engine = useEngine();
  const [busy, setBusy] = useState(false);
  const auto = actorLink(correction.autoActor, lookups);
  const by = actorLink(correction.by, lookups);
  const resolve = async (status: 'resolved' | 'ignored'): Promise<void> => {
    setBusy(true);
    try {
      await engine.corrections.resolve(correction.id, { status });
      showToast(status === 'resolved' ? 'Correction resolved' : 'Correction ignored');
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };
  const topic = correction.topicId ? lookups.topic(correction.topicId)?.name ?? correction.snapshot.topicNames[correction.topicId] ?? 'A topic' : null;
  const score = correction.topicId && correction.snapshot.scores ? correction.snapshot.scores[correction.topicId] : undefined;
  return (
    <section className="detail-section correction-section" aria-label="Correction">
      <div className="section-title">
        <h2>Correction</h2>
        <span className="correction-status">Open</span>
      </div>
      {correction.kind === 'placement' ? (
        <>
          <div className="correction-override" aria-label="Automatic placement and the override">
            <span className="correction-move" title="Where automation put it">
              <FolderIcon folder={lookups.folder(correction.fromFolderId)} />
              <strong>{lookups.folder(correction.fromFolderId)?.name ?? 'A folder'}</strong>
            </span>
            <Icon name="arrow-right" className="correction-arrow" />
            <span className="correction-move" title="Where it was moved">
              <FolderIcon folder={lookups.folder(correction.toFolderId)} />
              <strong>{lookups.folder(correction.toFolderId)?.name ?? 'A folder'}</strong>
            </span>
          </div>
          <p className="event-meta correction-who">
            <span>Placed by</span> <LinkButton link={auto} go={go} /> <span>· moved by</span>{' '}
            {by.to ? <LinkButton link={by} go={go} /> : <span>{by.label === 'You' ? 'you' : by.label}</span>}
          </p>
        </>
      ) : (
        <p className="subtle correction-topic">
          {topic} {correction.topicChange === 'removed' ? 'removed' : 'added'} by {by.label === 'You' ? 'you' : by.label}
          {score !== undefined ? ` · model score ${score}` : ''}
        </p>
      )}
      <div className="correction-actions">
        <button type="button" className="secondary-button" disabled={busy} onClick={() => void resolve('ignored')}>
          Ignore
        </button>
        <button type="button" className="primary-button" disabled={busy} onClick={() => void resolve('resolved')}>
          <Icon name="check" /> Resolve
        </button>
      </div>
    </section>
  );
}

export function DetailsPane({ row, detail, lookups, timeZone, scrollKey, onClose, go }: DetailsPaneProps) {
  const ref = useRef<HTMLDivElement>(null);
  const catalog = useCatalog();
  const download = useModelDownload();
  const installs = useInstalls();
  const onDeviceEntries = (catalog?.entries ?? []).filter(isOnDevice);
  const loaded = detail !== undefined && detail.message.id === row?.id;

  // Details fills in parts (the message, then the model data): restore once the content is tall enough.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    return restoreAndRememberScroll(el, scrollKey, 'details');
  }, [scrollKey, loaded]);

  if (!row) return <aside className="details-pane" aria-label="Details" />;

  const m = loaded ? detail.message : null;
  const folder = lookups.folder(m?.placement.folderId ?? row.folderId);
  const placedBy = m ? actorLink(m.placement.placedBy, lookups) : null;
  const results = m ? Object.entries(m.filterResults) : [];
  const pending = results.filter(([, v]) => v === null).length;
  const topics = m?.topics ?? row.topics;
  const modelScores = m?.modelScores ?? [];
  const scored = modelScores.filter((ms) => ms.scores !== null);
  const shownBy = (m?.scoringModel ?? row.scoredBy ?? null) || null;
  const waits = modelScores.filter((ms) => ms.state === 'pending');
  const onDeviceEntry = waits.some((w) => w.waiting === 'download' || w.waiting === 'error') ? onDeviceEntries.find((e) => waits.some((w) => w.modelId === e.id)) : undefined;
  const installError = onDeviceEntry ? installs.byId.get(onDeviceEntry.id) : undefined;
  const stats = loaded ? detail.sourceStats : null;
  const timeline = loaded ? orderTimeline(detail.timeline) : [];
  const open = loaded ? detail.corrections.filter((c) => c.status === 'open') : [];
  const assessment = loaded ? detail.assessment : null;

  return (
    <aside className="details-pane" aria-label="Details">
      <div className="pane-heading">
        <span>Details</span>
        <Tool icon="x" label="Close details" onClick={onClose} />
      </div>
      <div className="details-scroll" ref={ref}>
        <section className="placement">
          <div className="placement-top">
            <span className="folder-tile">
              <FolderIcon folder={folder} />
            </span>
            <div>
              <small>Current folder</small>
              <strong>{folder?.name ?? ''}</strong>
            </div>
          </div>
          <div className="placement-by">
            <span>Moved by</span>
            {placedBy ? placedBy.to ? <LinkButton link={placedBy} go={go} /> : <strong>{placedBy.label}</strong> : <strong>…</strong>}
          </div>
        </section>

        {open.map((c) => (
          <CorrectionSection key={c.id} correction={c} lookups={lookups} go={go} />
        ))}

        <section className="detail-section" data-section="topics">
          <h2>Topics</h2>
          {topics.length ? (
            topics.map((t) => {
              const topic = lookups.topic(t.topicId);
              if (!topic) return null;
              const perModel = scored.length > 1 ? scored.filter((ms) => ms.scores && ms.scores[t.topicId] !== undefined) : [];
              return (
                <button type="button" className={perModel.length ? 'topic-detail per-model' : 'topic-detail'} key={t.topicId} onClick={() => go(settingsPath.topic(t.topicId))}>
                  <span className="topic-symbol" style={{ color: topic.color }}>
                    <Icon name={topic.icon} />
                  </span>
                  <span>
                    {topic.name}
                    {perModel.length ? (
                      <small className="topic-model-scores" data-model-scores={t.topicId}>
                        {perModel.map((ms) => `${lookups.modelShort(ms.modelId)} ${ms.scores?.[t.topicId] ?? '—'}`).join(' · ')}
                      </small>
                    ) : null}
                  </span>
                  <b title={t.score === null ? 'Manually assigned · no model score' : `Topic relevance out of 100${shownBy ? ` · ${lookups.modelName(shownBy)}` : ''}`}>{t.score ?? '—'}</b>
                </button>
              );
            })
          ) : (
            <p className="subtle">{row.scoreState === 'skipped' ? 'Skipped by routing rule' : row.scoreState === 'failed' ? 'Scoring failed' : row.scoreState === 'done' || scored.length > 0 ? 'No topic reached the display floor' : waits.length ? 'Not scored yet' : 'Not evaluated yet'}</p>
          )}
          {shownBy ? (
            <div className="scored-by" data-scored-by={shownBy}>
              <span>Scored by</span>
              <button type="button" onClick={() => go(settingsPath.model(shownBy))}>
                {lookups.modelName(shownBy)} <Icon name="external-link" />
              </button>
            </div>
          ) : null}
          {waits.map((w) => (
            <p className="subtle model-wait" key={w.modelId} data-waiting={w.modelId} data-reason={w.waiting ?? 'queued'}>
              <Icon name={w.waiting === 'download' ? 'download' : w.waiting === 'power' ? 'battery-low' : w.waiting === 'unavailable' || w.waiting === 'error' ? 'circle-alert' : 'clock'} />
              <span>
                {waitText(w.waiting, lookups.modelName(w.modelId), lookups.onDevice(w.modelId))}
                {w.waiting === 'download' && onDeviceEntry ? (
                  <>
                    {' · '}
                    <button type="button" className="text-link" data-action="download-model" onClick={() => download(onDeviceEntry)}>
                      Download ({megabytes(onDeviceEntry.downloadBytes)})
                    </button>
                  </>
                ) : null}
                {w.waiting === 'error' && onDeviceEntry ? (
                  <>
                    {installError?.phase === 'error' ? `. ${installErrorText(installError)}` : null}
                    {' · '}
                    <button type="button" className="text-link" data-action="retry-model" onClick={() => download(onDeviceEntry)}>
                      Retry
                    </button>
                  </>
                ) : null}
              </span>
            </p>
          ))}
        </section>

        <section className="detail-section">
          <div className="section-title">
            <h2>Source history</h2>
            <button type="button" aria-label="Open source settings" title="Open source settings" onClick={() => go(settingsPath.source(row.sourceId))}>
              <Icon name="external-link" />
            </button>
          </div>
          <div className="credibility">
            <strong>{stats && stats.received > 0 && stats.usefulness !== null ? stats.usefulness.toFixed(3) : '—'}</strong>
            <span>{stats && stats.received > 0 ? 'Personal usefulness' : 'No history yet'}</span>
          </div>
          <p className="subtle">
            {formatExact(stats?.kept ?? 0)} kept <span className="dot">·</span> {formatExact(stats?.received ?? 0)} received
          </p>
        </section>

        <section className="detail-section">
          <div className="section-title">
            <h2>History</h2>
            <span>{timeline.length}</span>
          </div>
          <ol className="timeline">
            {timeline.map((h) => {
              const look = h.kind === 'scored' ? { cls: 'model', icon: h.modelId && lookups.onDevice(h.modelId) ? 'globe' : 'sparkles' } : TIMELINE_ICON[h.actor.kind] ?? { cls: 'user', icon: 'user' };
              const link = h.kind === 'filters-evaluated' ? null : h.kind === 'scored' && h.modelId ? { label: lookups.modelName(h.modelId), to: settingsPath.model(h.modelId) } : actorLink(h.actor, lookups);
              return (
                <li key={h.key} data-kind={h.kind} {...(h.modelId ? { 'data-model': h.modelId } : {})}>
                  <span className={`timeline-icon ${look.cls}`}>
                    <Icon name={look.icon} />
                  </span>
                  <div>
                    <strong>{h.title}</strong>
                    {h.detail ? <p>{h.detail}</p> : null}
                    <div className="event-meta">
                      <time dateTime={new Date(h.at).toISOString()}>{eventTime(h.at, timeZone)}</time>
                      {link?.to ? <LinkButton link={link} go={go} /> : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        <section className="detail-section disclosure">
          <details>
            <summary>
              Filter results <span>{pending ? `${pending} pending` : `${results.length} evaluated`}</span>
            </summary>
            <div className="filter-results">
              {results.length ? (
                results.map(([id, value]) => (
                  <button type="button" key={id} data-filter={id} onClick={() => go(settingsPath.filter(id))}>
                    <span>{lookups.rule(id)?.name ?? 'Deleted filter'}</span>
                    <span className={`result${value === 1 ? ' matched' : ''}`}>{value === null ? 'Pending' : value === 1 ? 'Matched' : 'No match'}</span>
                  </button>
                ))
              ) : (
                <p className="subtle">No filter applies to this folder.</p>
              )}
            </div>
          </details>
        </section>

        <section className="detail-section disclosure">
          <details>
            <summary>
              Model assessment <span>{assessment ? 'Available' : 'None'}</span>
            </summary>
            {assessment ? (
              <>
                <p className="assessment-copy">{assessment.body}</p>
                <button type="button" className="small-link" onClick={() => go(assessment.by.kind === 'mcp' && assessment.by.id ? settingsPath.connector(assessment.by.id) : settingsPath.assistant())}>
                  Connected assistant · {eventTime(assessment.createdAt, timeZone)} <Icon name="external-link" />
                </button>
              </>
            ) : (
              <p className="subtle">No assessment has been added.</p>
            )}
          </details>
        </section>
      </div>
    </aside>
  );
}
